import React from "react";
import PageHeader from "../components/PageHeader";
import SectionTitle from "../components/SectionTitle";
import CtaBanner from "../components/CtaBanner";
import ProjectImagesGallery, {
  projectGallerySkeletonClass,
} from "../components/ProjectImagesGallery";
import { useAbout, usePageBanner } from "../api/queries";
import { AlertCircle, Building2 } from "lucide-react";

const DEFAULT_SECTION = {
  tagline: "Our Portfolio",
  title: "Engineering India's Landmark Projects",
  subtitle:
    "From power plants and refineries to metro systems and hydro projects — every assignment delivered with precision and uncompromising quality.",
};

export default function ProjectsPage() {
  const { data: about, isLoading: loading, error: aboutError, refetch } = useAbout();
  const { banner: projectBanner, isLoading: bannerLoading } = usePageBanner("project");

  const projectImages = about?.projectImageUrls || [];
  const error = aboutError?.message || null;
  const galleryTitle = projectBanner?.title || DEFAULT_SECTION.title;

  return (
    <>
      <PageHeader
        title={galleryTitle}
        image={projectBanner?.image}
        isLoading={bannerLoading}
        breadcrumb="Projects"
      />

      <section className="relative overflow-hidden py-16 md:py-24">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-slate-50" />
        <div className="pointer-events-none absolute -right-24 top-0 h-72 w-72 rounded-full bg-[#1e6fcf]/8 blur-3xl" />
        <div className="pointer-events-none absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-[#aad4fa]/30 blur-3xl" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <div className="mb-8 md:mb-10">
            <SectionTitle
              eyebrow={DEFAULT_SECTION.tagline}
              title={DEFAULT_SECTION.title}
              subtitle={DEFAULT_SECTION.subtitle}
            />
          </div>

          {loading ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 lg:gap-6">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <div key={n} className={projectGallerySkeletonClass} />
              ))}
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 py-16 text-center">
              <AlertCircle className="mx-auto mb-4 h-12 w-12 text-red-400" />
              <p className="font-semibold text-red-700">{error}</p>
              <button
                type="button"
                onClick={() => refetch()}
                className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#1758a8] hover:underline"
              >
                Try again
              </button>
            </div>
          ) : projectImages.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center shadow-sm">
              <Building2 className="mx-auto mb-4 h-12 w-12 text-[#1e6fcf]/40" />
              <p className="font-semibold text-slate-600">No project images available yet.</p>
            </div>
          ) : (
            <ProjectImagesGallery images={projectImages} title={galleryTitle} />
          )}
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
